'use client';

import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import MembershipCard from './MembershipCard'; 

/* --- COLORS --- */
const COLORS = {
  GOLD_ACCENT: '#faaf40',
  DARK_BG: '#050505', 
  ACCENT_GOLD: '#faaf40',
};

/* --- DATA UPDATED FROM IMAGE --- */
const plans = [
  { 
    title: 'KINETIC CORE', 
    subtitle: 'The Ultimate Plan', 
    price: '199', 
    oldPrice: '400',
    promise: 'Maximum Efficiency.',
    features: [
      '8 Precision EMS Sessions (2x Weekly)', 
      'Full Body Composition Analysis', 
      'Active Recovery Protocols',
      'Dedicated 1-on-1 Trainer'
    ],
    footerInfo: 'LIMITED TIME OFFER',
    isHighlight: false
  },
  { 
    title: 'METABOLIC SCULPT', 
    subtitle: 'The Maintenance Plan', 
    price: '275', 
    oldPrice: '550',
    promise: 'Rapid Visual Definition.',
    features: [
      '12 Precision EMS Sessions (2x Weekly)', 
      'Full Body Composition Analysis', 
      'Active Recovery Protocols',
      'Dedicated 1-on-1 Trainer'
    ],
    footerInfo: 'LIMITED TIME OFFER',
    subFooter: '(Most Popular)',
    isHighlight: true
  },
  { 
    title: 'SIGNATURE SANCTUARY', 
    subtitle: 'The Executive', 
    price: '375', 
    oldPrice: '750', 
    promise: 'Total Holistic Care.',
    features: [
      '16 High-Performance Sessions', 
      'Concierge Booking (Priority Slots)', 
      'Private Suite Access',
      'Elite Conditioning & Massage Gun Therapy'
    ],
    footerInfo: 'LIMITED TIME OFFER',
    isHighlight: false
  },
];

/* --- MAIN SECTION COMPONENT --- */
export default function MembershipPlansSection() {
  const [currentIndex, setCurrentIndex] = useState(1); // Start Center
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const totalPlans = plans.length;
  const dragThreshold = 50;

  const nextCard = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalPlans);
  }, [totalPlans]);

  const prevCard = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalPlans) % totalPlans);
  }, [totalPlans]);

  const getPosition = (index) => {
    const relativeIndex = (index - currentIndex + totalPlans) % totalPlans;
    if (relativeIndex === 0) return 'front';
    if (relativeIndex === 1) return 'right';
    if (relativeIndex === totalPlans - 1) return 'left';
    return 'back';
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    setStartX(clientX);
  };

  const handleDragEnd = (e) => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const endX = e.clientX !== undefined 
      ? e.clientX 
      : (e.changedTouches && e.changedTouches[0].clientX);
      
    if (endX !== undefined) {
      const deltaX = endX - startX;
      if (deltaX > dragThreshold) {
        prevCard();
      } else if (deltaX < -dragThreshold) {
        nextCard();
      }
    }
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
  };

  return (
    <section className="relative py-12 md:py-20 bg-black text-white overflow-hidden min-h-screen flex flex-col justify-center">
      
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${COLORS.DARK_BG} 0%, #1a1a1a 20%, #402313 40%, ${COLORS.ACCENT_GOLD} 50%, #d98d4a 60%, #402313 70%, #1a1a1a 90%, ${COLORS.DARK_BG} 100%)`,
          opacity: 0.25,
          filter: 'blur(60px)',
        }}
      />

      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at center, transparent 0%, #000000 90%)' }} />

      <div className="max-w-7xl mx-auto px-4 text-center mb-8 relative z-10">
        <h1 className="text-3xl md:text-5xl font-extrabold mb-4 uppercase" style={{ color: COLORS.GOLD_ACCENT }}>
          PREMIUM MEMBERSHIP PLANS
        </h1>
        <p className="text-sm md:text-lg max-w-2xl mx-auto text-gray-500">
          Choose the perfect plan to reach your fitness goals with premium benefits and exclusive offers in Islamabad.
        </p>
      </div>

      <div
        className="relative flex justify-center items-center h-[650px] cursor-grab select-none w-full overflow-hidden touch-pan-y z-10"
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
        onMouseLeave={isDragging ? handleDragEnd : undefined}
        onMouseMove={handleDragMove}
        onTouchStart={handleDragStart}
        onTouchEnd={handleDragEnd}
        onTouchMove={handleDragMove}
      >
        {plans.map((plan, index) => (
          <MembershipCard key={plan.title} plan={plan} position={getPosition(index)} />
        ))}

        <button onClick={prevCard} className="group absolute left-4 md:left-[15%] top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-[#faaf40] hover:bg-[#faaf40] hover:text-black z-50 transition-all">
          <ChevronLeft className="w-6 h-6" />
        </button>

        <button onClick={nextCard} className="group absolute right-4 md:right-[15%] top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-[#faaf40] hover:bg-[#faaf40] hover:text-black z-50 transition-all">
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>
    </section>
  );
}